import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal } from "./Reveal";

export const Testimonials = () => {
  const { t } = useTranslation();
  const reviews = t("testimonials.reviews", { returnObjects: true });
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);

  const go = (d) => {
    setDir(d);
    setIndex((i) => (i + d + reviews.length) % reviews.length);
  };

  const r = reviews[index];

  return (
    <section id="recensioni" className="relative bg-[#0D0F12] px-6 py-20 lg:px-12 lg:py-28" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl">
        <div className="flex items-end justify-between gap-8">
          <Reveal>
            <p className="font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]">{t("testimonials.overline")}</p>
            <h2 className="mt-4 max-w-xl font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl">
              {t("testimonials.h2pre")} <em className="italic text-[#D6705A]">{t("testimonials.h2em")}</em>{t("testimonials.h2post")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex gap-3">
              <button
                onClick={() => go(-1)}
                aria-label={t("testimonials.prevAria")}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-[#A0A0A0] transition-[border-color,color,background-color] duration-300 hover:border-[#D6705A] hover:text-[#D6705A]"
                data-testid="testimonial-prev"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => go(1)}
                aria-label={t("testimonials.nextAria")}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-[#A0A0A0] transition-[border-color,color,background-color] duration-300 hover:border-[#D6705A] hover:text-[#D6705A]"
                data-testid="testimonial-next"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative mt-12" data-testid="testimonial-carousel">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.figure
                key={`${index}-${r.name}`}
                initial={{ opacity: 0, x: 48 * dir }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -48 * dir }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-[#16191D] p-8 sm:p-12"
                data-testid="testimonial-slide"
              >
                <blockquote className="font-display text-xl font-light italic leading-relaxed text-[#F9F8F6] sm:text-2xl" data-testid="testimonial-quote">
                  “{r.quote}”
                </blockquote>
                <figcaption className="mt-8 flex items-center gap-4">
                  <span className="h-px w-10 bg-[#D6705A]" />
                  <div>
                    <p className="text-sm text-[#F9F8F6]" data-testid="testimonial-author">{r.name}</p>
                    <p className="mt-0.5 font-code text-[10px] uppercase tracking-[0.2em] text-[#666]" data-testid="testimonial-role">{r.role}</p>
                  </div>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>
        </Reveal>

        <div className="mt-10 flex items-center justify-between border-t border-white/5 pt-6">
          <p className="font-code text-[10px] uppercase tracking-[0.3em] text-[#666]" data-testid="testimonial-counter">
            {String(index + 1).padStart(2, "0")} — {String(reviews.length).padStart(2, "0")}
          </p>
          <div className="flex gap-1.5">
            {reviews.map((rev, i) => (
              <span
                key={rev.name}
                className={`h-1 rounded-full transition-[width,background-color] duration-300 ${i === index ? "w-8 bg-[#D6705A]" : "w-3 bg-white/15"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
